<script lang="ts">
  import type { TreeEntry } from '$lib/core/api';
  import type { EditorState } from '../editor-state.svelte';

  const { editor }: { editor: EditorState } = $props();

  const tree = $derived.by<TreeEntry[]>(() => {
    void editor.document;
    return editor.drawing.tree();
  });

  function onRowClick(event: MouseEvent, id: string) {
    if (event.shiftKey) editor.toggleSelected(id);
    else {
      editor.select([id]);
      editor.showSource(id);
    }
  }
</script>

{#snippet row(entry: TreeEntry, depth: number)}
  <li>
    <div
      class="row"
      class:selected={editor.selection.includes(entry.id)}
      style:padding-left="{depth * 14 + 8}px"
    >
      <button type="button" class="label" onclick={(event) => onRowClick(event, entry.id)}>
        <span class="name">{entry.name}</span>
        <span class="type">{entry.type}</span>
        {#if entry.error}<span class="error" title={entry.error}>!</span>{/if}
      </button>
    </div>
    {#if entry.children.length > 0}
      <ul>
        {#each entry.children as child (child.id)}
          {@render row(child, depth + 1)}
        {/each}
      </ul>
    {/if}
  </li>
{/snippet}

{#if tree.length === 0}
  <p class="empty">Nothing drawn yet.</p>
{:else}
  <ul class="root">
    {#each tree as entry (entry.id)}
      {@render row(entry, 0)}
    {/each}
  </ul>
{/if}

<style>
  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .root {
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding-bottom: var(--space-2);
  }

  .row {
    display: flex;
    align-items: center;
    gap: 4px;
    padding-right: var(--space-2);
  }

  .row.selected {
    background: color-mix(in srgb, var(--color-accent) 18%, transparent);
  }

  .label {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex: 1;
    padding: 3px 0;
    border: 0;
    background: none;
    color: var(--color-text);
    font: inherit;
    font-size: 0.8125rem;
    text-align: left;
    cursor: pointer;
  }

  .type {
    font-size: 0.6875rem;
    color: var(--color-text-muted);
  }

  .error {
    color: #e03131;
    font-weight: 700;
  }

  .empty {
    margin: 0;
    padding: 0 var(--space-3) var(--space-3);
    font-size: 0.8125rem;
    color: var(--color-text-muted);
  }
</style>
